/*
 * Pure per-player metric builders — single-entity versions of the data-shaping
 * the In Depth tabs do over the multi-entity `entities` array. Visão Geral uses
 * them to surface one player's headline numbers with the SAME logic (and so the
 * same values) as Em Detalhe. No React, no i18n: just data in, data out.
 */
import type { PlayerStatistics } from '@/types/statistics';
import {
  selectShotConsistency,
  selectShots,
  SHOT_RADAR_AXES,
  type ShotConsistency,
} from './shotTypes';

/**
 * Court occupancy folded to six zones (net / back × left / mid / right; the thin
 * transition band merges into back) — sums to 100%. Order matches the position
 * hexagon's axes (see zoneAxes in MovementTab / OverviewPage). Returns null when
 * there's no occupancy to draw.
 */
export function playerZoneValues(ps: PlayerStatistics): number[] | null {
  const z = ps.court_zone?.zones;
  if (!z) return null;
  const g = (k: string) => z[k] ?? 0;
  const values = [
    g('net_middle'),
    g('net_right'),
    g('back_right') + g('transition_right'),
    g('back_middle') + g('transition_middle'),
    g('back_left') + g('transition_left'),
    g('net_left'),
  ];
  return values.every((v) => v <= 0) ? null : values;
}

/** Shape of the slice of shots_breakdown the per-player builders read. */
interface ShotsBreakdownLike {
  players?: Record<
    string,
    {
      summary?: {
        offensive_total_ratio?: number;
        defensive_total_ratio?: number;
      };
    }
  >;
}

/**
 * Offensive vs defensive split (0..1 each) from
 * shots_breakdown.players[id].summary — the Estilo de Jogo numbers. Returns null
 * when the player has no breakdown.
 */
export function playerStyleRatios(
  shotsBreakdown: unknown,
  playerId: string,
): { offensive: number; defensive: number } | null {
  const sb = shotsBreakdown as ShotsBreakdownLike | null | undefined;
  const r = sb?.players?.[playerId]?.summary;
  if (!r || typeof r.offensive_total_ratio !== 'number') return null;
  return {
    offensive: r.offensive_total_ratio,
    defensive: r.defensive_total_ratio ?? 0,
  };
}

/**
 * In-play vs error vs winner rates (0..1) over all the player's rally shots.
 * Returns null when there are none (so the card can hide gracefully).
 *
 * Literally the In Depth "all shots" chip — same function, so Visão Geral can
 * never print a different number to Em Detalhe for the same player.
 */
export function playerConsistency(
  ps: PlayerStatistics,
): ShotConsistency | null {
  return selectShotConsistency(ps, 'all');
}

/** Accepted rally-shot count (serves dropped, smash folded) — matches the chips. */
export const playerShotTotal = (ps: PlayerStatistics): number =>
  selectShots(ps, 'all').length;

// Radar axis → which ratio key(s) it sums and which group it reads from. Order
// MUST match the AXES order in ShotRadar (the values array is positional). The
// overhead axis absorbs any separate "smash" key (some feeds split the two).
export interface ShotRatioSource {
  offensive_shots_ratio?: Record<string, number>;
  defensive_shots_ratio?: Record<string, number>;
}

/** Shared seven-axis extraction for player, team, and radar consumers. */
export function shotRadarValuesForSource(src: ShotRatioSource): number[] {
  const offR = src.offensive_shots_ratio ?? {};
  const defR = src.defensive_shots_ratio ?? {};
  return SHOT_RADAR_AXES.map((axis) => {
    const ratios = axis.group === 'offensive' ? offR : defR;
    return axis.backendKeys.reduce((sum, key) => sum + (ratios[key] ?? 0), 0);
  });
}

/**
 * Per-shot-type mix for the pentagon: each axis is the type's share WITHIN its
 * group (offensive axes from offensive_shots_ratio, defensive from
 * defensive_shots_ratio), taken straight from shots_breakdown.players[id].
 * Returns null when the player has no usable breakdown.
 */
export function playerShotRadarValues(
  shotsBreakdown: unknown,
  playerId: string,
): number[] | null {
  const sb = shotsBreakdown as
    | { players?: Record<string, ShotRatioSource> }
    | null
    | undefined;
  const src = sb?.players?.[playerId];
  if (!src) return null;
  const values = shotRadarValuesForSource(src);
  return values.every((v) => v <= 0) ? null : values;
}

/**
 * The other member(s) of `playerId`'s team. Padel teams are pairs, so this is
 * normally a single partner; returns [] when the player or their team is unknown.
 */
export function teammatesOf(
  teams: Record<string, { teammates: string[] }>,
  playerId: string,
): string[] {
  for (const info of Object.values(teams)) {
    if (info.teammates?.includes(playerId)) {
      return info.teammates.filter((id) => id !== playerId);
    }
  }
  return [];
}

export interface ResolvedTeams {
  yourTeamId: string;
  oppTeamId: string;
  /** Focus player's team, focus player listed first. */
  yourMembers: string[];
  /** The opposing team's members, in their stored order. */
  oppMembers: string[];
}

/**
 * Resolve the focus player's team and the opposing team from a segment's teams
 * map. Returns null when the focus player belongs to no team or there's no
 * opposing team. Shared by the cards that compare "your team" vs "opponents".
 */
export function resolveTeams(
  teams: Record<string, { teammates: string[] }>,
  focusPlayerId: string,
): ResolvedTeams | null {
  const ids = Object.keys(teams);
  const yourTeamId = ids.find(
    (id) => teams[id]?.teammates?.includes(focusPlayerId),
  );
  const oppTeamId = ids.find((id) => id !== yourTeamId);
  if (!yourTeamId || !oppTeamId) return null;
  const your = teams[yourTeamId].teammates;
  return {
    yourTeamId,
    oppTeamId,
    yourMembers: [focusPlayerId, ...your.filter((id) => id !== focusPlayerId)],
    oppMembers: teams[oppTeamId].teammates,
  };
}
